import { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { useAppDispatch, useAppSelector } from 'src/store';
import { setDateRange as setStoreDateRange, setDateRangeX } from 'src/store/slices/analyticSlices';
import { DateItems } from 'src/pages/AnalyticsPage/AnalyticsCustomer/DateItems';
import { ISO_DATE_FORMAT } from 'src/shared/constants';
import {
  AggregatedRequestParams,
  DateRangeItem,
  TimeSeriesRequestParams,
} from 'src/pages/AnalyticsPage/AnalyticsCustomer/AnalyticsCoCustomer.types';

const defaultDateItem = (DateItems.find((item) => item.default) || DateItems[0]) as DateRangeItem;

/**
 * Owns the selected date range, keeps it in sync with redux (analyticSlices),
 * and derives the request params consumed by the aggregated / time-series queries.
 */
export const useCoCustomerDateRange = (projectId: string) => {
  const dispatch = useAppDispatch();
  const { start_time, end_time, group_by, dateRangeItem, timezone } = useAppSelector(
    (s) => s.analyticSlices,
  );
  const initialDateRange = (
    dateRangeItem ? DateItems.find((item) => item.label === dateRangeItem.label) : defaultDateItem
  ) as DateRangeItem;
  const [dateRange, setDateRange] = useState<DateRangeItem>(initialDateRange);

  const [aggregatedRequestParams, setAggregatedRequestParams] = useState<AggregatedRequestParams>({
    project_ids: [projectId],
    start_time: start_time || defaultDateItem.getStartTime(),
    end_time: end_time || defaultDateItem.getEndTime(),
    changes_percentage: true,
  });

  const [timeSeriesRequestParams, setTimeSeriesRequestParams] = useState<TimeSeriesRequestParams>({
    project_ids: [projectId],
    start_time: start_time || defaultDateItem.getStartTime(),
    end_time: end_time || defaultDateItem.getEndTime(),
    group_by: group_by || defaultDateItem.groupBy,
    tz_offset: timezone.offset * 60,
  });

  useEffect(() => {
    const isCustom = dateRange.label === 'Custom';
    const dateItem = DateItems.find((item) => item.label === dateRange.label) as DateRangeItem;
    const startTime = isCustom ? dateRange.start_time : dateItem.getStartTime();
    const endTime = isCustom ? dateRange.end_time : dateItem.getEndTime();

    const startTzTimeUtc = moment.utc(startTime || start_time, ISO_DATE_FORMAT);
    const endTzTimeUtc = moment.utc(endTime || end_time, ISO_DATE_FORMAT);

    let groupBy = dateItem.groupBy;

    if (startTime && endTime) {
      if (endTzTimeUtc.diff(startTzTimeUtc, 'hours') < 24) {
        groupBy = 'hour';
      }
      dispatch(
        setStoreDateRange({
          start_time: startTime,
          end_time: endTime,
          dateRangeItem: dateItem,
          group_by: groupBy,
        }),
      );
      dispatch(setDateRangeX({ dateX: groupBy }));
    }

    const startTzTime = startTzTimeUtc.clone().tz(timezone.value).format(ISO_DATE_FORMAT);
    const endTzTime = endTzTimeUtc.clone().tz(timezone.value).format(ISO_DATE_FORMAT);

    setAggregatedRequestParams((prev) => ({
      ...prev,
      start_time: startTzTime,
      end_time: endTzTime,
    }));

    setTimeSeriesRequestParams((prev) => ({
      ...prev,
      start_time: isCustom ? startTime : dateItem.getStartTime(),
      end_time: isCustom ? endTime : dateItem.getEndTime(),
      group_by: isCustom ? group_by : dateItem.groupBy,
      tz_offset: timezone.offset * 60,
    }));
  }, [dateRange, timezone]);

  return {
    dateRange,
    setDateRange,
    aggregatedRequestParams,
    timeSeriesRequestParams,
  };
};
